import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Alert,
} from 'reactstrap';
import {
  addNewOfferData,
  editCurrOfferData,
  offerDetailsFetchData,
  newOfferSaveErrored,
} from '../../actions/offerActions';
import { BeatLoader } from 'react-spinners';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import { useParams, useNavigate } from 'react-router-dom';

const OfferEditPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { campaignId, promotionId, offerId } = useParams();

  // Redux state
  const offer = useSelector((state) => state.offerDetails);
  const isSuccess = useSelector(
    (state) => state.isNewOfferSaveSuccess?.isSavedSuccess,
  );
  const savedOffer = useSelector((state) => state.offer);
  const hasErrored = useSelector((state) => state.isNewOfferSaveErrored);
  const errorMessage = useSelector(
    (state) => state.isNewOfferSaveErrored?.message || '',
  );
  const isSaving = useSelector((state) => state.isNewOfferIsSaving);
  const redirectToOfferListPage = useSelector(
    (state) => state.redirectToOfferListPage,
  );

  // Local state
  const [externalId, setExternalId] = useState('');
  const [source, setSource] = useState('');
  const [offerName, setOfferName] = useState('');
  const [offerDescription, setOfferDescription] = useState('');
  const [startDate, setStartDate] = useState(moment());
  const [endDate, setEndDate] = useState(moment());
  const [isFormValid, setIsFormValid] = useState(false);
  const [pageTitle, setPageTitle] = useState(offerId ? 'Edit' : 'Add New');
  const [isOfferIdEnabled, setIsOfferIdEnabled] = useState(!offerId);
  const [isReadOnly, setIsReadOnly] = useState(false);

  // Error flags
  const [errors, setErrors] = useState({
    source: false,
    offerName: false,
    offerDescription: false,
    startDate: false,
    endDate: false,
    status: false,
  });

  // Fetch offer data on mount
  useEffect(() => {
    dispatch(newOfferSaveErrored(false));
    if (offerId) {
      dispatch(offerDetailsFetchData(offerId));
      setIsFormValid(true);
      setPageTitle('Edit');
      setIsOfferIdEnabled(false);
    }
  }, [dispatch, offerId]);

  // Update local state when offer data changes
  useEffect(() => {
    if (offerId && offer) {
      setSource(offer.source || '');
      setOfferName(offer.name || '');
      setOfferDescription(offer.description || '');
      setStartDate(offer.startDate ? moment(offer.startDate) : moment());
      setEndDate(offer.endDate ? moment(offer.endDate) : moment());
      setExternalId(offer.externalId || '');
      setIsReadOnly(!!offer.isReadOnly);
    }
  }, [offerId, offer]);

  // Redirect if needed
  useEffect(() => {
    if (redirectToOfferListPage) {
      navigate(`/campaign/${campaignId}/promotions/${promotionId}`);
    }
  }, [redirectToOfferListPage, navigate, campaignId, promotionId]);

  // Form validation
  const validateForm = useCallback(
    (sDate = startDate, eDate = endDate) => {
      const newErrors = { ...errors };
      let valid = true;

      const isSDateEmpty = !sDate;
      const isEDateEmpty = !eDate;
      if (isSDateEmpty || isEDateEmpty) {
        newErrors.startDate = true;
        newErrors.endDate = true;
        valid = false;
      } else {
        const sDateFormatted = moment(sDate).format('DD-MMM-YYYY');
        const eDateFormatted = moment(eDate).format('DD-MMM-YYYY');
        const isSameOrBefore =
          moment(eDateFormatted).isSameOrBefore(sDateFormatted);
        if (isSameOrBefore) {
          alert('Invalid date');
          newErrors.startDate = true;
          newErrors.endDate = true;
          valid = false;
        } else {
          newErrors.startDate = false;
          newErrors.endDate = false;
        }
      }
      setErrors(newErrors);
      setIsFormValid(valid);
      return valid;
    },
    [startDate, endDate, errors],
  );

  // Handlers
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    switch (name) {
      case 'externalId':
        setExternalId(value);
        break;
      case 'source':
        setSource(value);
        break;
      case 'offerName':
        setOfferName(value);
        break;
      case 'offerDescription':
        setOfferDescription(value);
        break;
      default:
        break;
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    validateForm(date, endDate); // Pass the new date
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    validateForm(startDate, date); // Pass the new date
  };

  const canSubmitSearchForm = () => true; // Placeholder

  const handleSubmitAddForm = (e) => {
    e.preventDefault();
    if (!canSubmitSearchForm()) return;
    const newOffer = {
      externalId,
      source,
      name: offerName,
      description: offerDescription,
      startDate: startDate ? moment(startDate).format('DD-MMM-YYYY') : '',
      endDate: endDate ? moment(endDate).format('DD-MMM-YYYY') : '',
      IsActive: true,
      promotionId,
    };
    if (offerId) {
      newOffer.id = offerId;
      dispatch(editCurrOfferData(newOffer));
    } else {
      dispatch(addNewOfferData(newOffer));
    }
  };

  // Error message
  const errorMessageAlert = (
    <Row>
      <Col md={12}>
        <Alert color='danger'>{errorMessage}</Alert>
      </Col>
    </Row>
  );

  return (
    <div>
      <h4>{pageTitle} Offer</h4>
      <Card>
        <CardBody>
          <Container fluid>
            <Form onSubmit={handleSubmitAddForm}>
              {hasErrored && errorMessage.length > 0 ? errorMessageAlert : null}
              <Row>
                <Col>
                  <FormGroup row>
                    <Label for='externalId' md={1}>
                      Offer Id
                    </Label>
                    <Col md={2} sm={10}>
                      <Input
                        disabled={!isOfferIdEnabled || isReadOnly}
                        type='text'
                        name='externalId'
                        id='externalId'
                        value={externalId}
                        onChange={handleInputChange}
                        className={errors.source ? 'error' : ''}
                        maxLength='20'
                      />
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup row>
                    <Label for='offerName' md={1}>
                      Offer Name
                    </Label>
                    <Col md={5} sm={10}>
                      <Input
                        disabled={isReadOnly}
                        type='text'
                        name='offerName'
                        id='offerName'
                        value={offerName}
                        required
                        onChange={handleInputChange}
                        className={errors.source ? 'error' : ''}
                      />
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup row>
                    <Label for='offerDescription' md={1}>
                      Description
                    </Label>
                    <Col md={10} sm={11}>
                      <Input
                        disabled={isReadOnly}
                        type='text'
                        name='offerDescription'
                        id='offerDescription'
                        value={offerDescription}
                        required
                        onChange={handleInputChange}
                        className={errors.source ? 'error' : ''}
                      />
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup row>
                    <Label for='startDate' md={1}>
                      Start Date
                    </Label>
                    <Col md={10} sm={10}>
                      <DatePicker
                        disabled={isReadOnly}
                        id='startDate'
                        name='startDate'
                        required
                        selected={startDate}
                        dateFormat='DD-MMM-YYYY'
                        onChange={handleStartDateChange}
                        className={errors.startDate ? 'error' : 'form-control'}
                        locale='en-au'
                      />
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup row>
                    <Label for='endDate' md={1}>
                      End Date
                    </Label>
                    <Col md={10} sm={10}>
                      <DatePicker
                        disabled={isReadOnly}
                        id='endDate'
                        name='endDate'
                        required
                        selected={endDate}
                        dateFormat='DD-MMM-YYYY'
                        onChange={handleEndDateChange}
                        className={errors.endDate ? 'error' : 'form-control'}
                      />
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup className='me-sm-2 mb-sm-0'>
                <Button
                  color='primary'
                  name='btnFilter'
                  id='btnFilter'
                  disabled={!isFormValid || isReadOnly}
                >
                  Save
                </Button>
                <BeatLoader color={'#004C5F'} loading={isSaving} />
              </FormGroup>
            </Form>
          </Container>
        </CardBody>
      </Card>
    </div>
  );
};

export default OfferEditPage;
